import React, { useEffect, useMemo, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import PropTypes from "prop-types";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { InputLabel } from "../InputLabel";
import { HubIcon } from "../HubIcon";
import { HubContextMenu } from "../HubContextMenu";

import HubTimePicker from "./HubTimePicker";
import HubTimePickerRender from "./HubTimePickerRender";

import { convertMinutesToTime, convertTimeToMinutes } from "./helper";
import { StyledFormControl } from "./styles";
import { Stack } from "@mui/material";

const DefaultIcon = ({ ...rest }) => (
  <HubIcon
    icon="Clock"
    sx={{
      marginRight: "10px",
      marginTop: "6px",
      cursor: "pointer",
    }}
    size={12}
    {...rest}
  />
);

export const HubHookFormTimePicker = ({
  name = "",
  defaultValue = 0,
  required = false,
  rules = {},
  disabled = false,
  onChange = null,
  isLoading = false,
  label = "",
  helperText = "",
  disableHelperText = true,
  smallLabel = true,
  hidden = false,
  fullWidth = true,
  fields = ["days", "hours", "minutes", "seconds"],
  shouldUnregister = true,
  formControlProps = {},
  ...rest
}) => {
  const id = `HubHookField-${name}`;
  const { setValue, register } = useFormContext();
  const {
    field: { onChange: onControllerChange, value },
    fieldState: { error },
  } = useController({
    name,
    rules: { required, ...rules },
    defaultValue,
    shouldUnregister,
  });

  const initialTime = useMemo(
    () => convertMinutesToTime(value || defaultValue),
    [defaultValue, value]
  );

  const [time, setTime] = useState(initialTime);

  useEffect(() => {
    setValue(name, convertTimeToMinutes(time)); // eslint-disable-next-line
  }, [time]);

  const handleTimeChange = (field, val) => {
    const newTime = { ...time, [field]: val };
    const newMinutes = convertTimeToMinutes(newTime);
    const initTime = convertMinutesToTime(newMinutes);
    setTime(initTime);
    onControllerChange(newMinutes);
    onChange && onChange(newMinutes);
  };

  const errorMessage = error?.message;
  const helperTextMessage = disableHelperText
    ? null
    : errorMessage || helperText || null;

  const RenderProps = { time, fields };

  const button = (
    <Stack
      width="100%"
      height="36.5px"
      padding="10px 15px"
      border="1px solid"
      borderColor="hubColors.greyLight"
      borderRadius="7px"
      alignItems="center"
      direction="row"
      justifyContent="space-between"
      cursor="pointer"
    >
      <HubTimePickerRender {...RenderProps} />
      <HubIcon icon="Clock" size="12px" />
    </Stack>
  );

  return (
    <StyledFormControl
      hidden={hidden}
      fullWidth={fullWidth}
      variant="outlined"
      {...formControlProps}
      {...rest}
    >
      <InputLabel
        label={label}
        required={required}
        id={id}
        smallLabel={smallLabel}
      />

      <HubContextMenu
        id={id}
        menuButton={button}
        IconButtonProps={{ sx: { padding: 0 } }}
        disabled={disabled}
        MenuProps={{ sx: { minWidth: "350px" } }}
        {...rest}
      >
        <HubTimePicker handleTimeChange={handleTimeChange} {...RenderProps} />
      </HubContextMenu>

      {isLoading && (
        <InputAdornment position="end">
          <CircularProgress size={24} />
        </InputAdornment>
      )}
      {helperTextMessage && (
        <FormHelperText error={!!error} id={`${id}-helper-text`}>
          {helperTextMessage}
        </FormHelperText>
      )}
      <input
        {...register(name)}
        type="hidden"
        value={convertTimeToMinutes(time)}
      />
    </StyledFormControl>
  );
};

HubHookFormTimePicker.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  rules: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  helperText: PropTypes.string,
  disableHelperText: PropTypes.bool,
  smallLabel: PropTypes.bool,
  hidden: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

export default HubHookFormTimePicker;
